import dayjs from 'dayjs';

import type {CourseApiModel} from '../../../api/course';
import type {ApiListResponse, ApiMethodName} from '../../../api/types';
import type {
  CoreClientConferencesListParams,
  CoreCoachConferencesListParams,
} from '../../../api/types/core/methods';
import type {AsyncStrategy} from '../utils';

import {
  type ConferenceApiModel,
  conferenceExpandCommon,
  type ConferenceExpandedCommon,
} from '../../../api/conference';
import {apiRequest} from '../../../api/utils/apiRequest';
import {
  datetimeObjToISOString,
  ISOStringToDatetimeObj,
} from '../../../utils/datetime';
import {isConferenceExpired} from '../../../utils/entity/conference';
import {isMeCoachForEntity} from '../../../utils/entity/isMeCoachForEntity';
import {isDefined} from '../../../utils/typecheck';

export {getCourseNextUpcomingConference};

type Config = {
  entity: CourseApiModel;
};

const strategy: AsyncStrategy<Config> = async (roles, config) => {
  if (isMeCoachForEntity(config.entity)) {
    return roles.coach(config);
  } else {
    return roles.client(config);
  }
};

async function getCourseNextUpcomingConference(config: Config) {
  return strategy({client: asClient, coach: asCoach}, config);
}

getCourseNextUpcomingConference.asClient = asClient;
getCourseNextUpcomingConference.asCoach = asCoach;

async function asClient({entity}: Config) {
  return getNextUpcomingConference(entity, false);
}

async function asCoach({entity}: Config) {
  return getNextUpcomingConference(entity, true);
}

const getNextUpcomingConference = async (
  course: CourseApiModel,
  forCoach = false,
) => {
  const method: ApiMethodName = forCoach
    ? 'coach.conferences.list'
    : 'client.conferences.list';

  const {_items: conferences}: ApiListResponse<ConferenceExpandedCommon> =
    await apiRequest<
      CoreClientConferencesListParams | CoreCoachConferencesListParams
    >({
      method,
      params: {
        expand: conferenceExpandCommon,
        query: [
          ['status', 'in', ['upcoming', 'ongoing']],
          ['course_id', '==', course._id],
          [
            'start_date',
            '>=',
            // we need this to include conferences that could be ongoing
            ISOStringToDatetimeObj(dayjs().subtract(6, 'hours').toISOString()),
          ],
        ],
      },
    });

  const conference = getClosestConference(conferences);

  if (!isDefined(conference)) {
    return null;
  }

  return conference;
};

const getClosestConference = <T extends ConferenceApiModel>(
  conferences: T[],
) => {
  if (conferences.length === 0) {
    return null;
  }

  const notExpiredConferences = conferences.filter(
    c => !isConferenceExpired(c),
  );

  if (notExpiredConferences.length === 0) {
    return null;
  }

  const now = dayjs().unix();

  const closest = notExpiredConferences.reduce((prev, curr) => {
    const currTimestamp = dayjs(datetimeObjToISOString(curr.start_date)).unix();
    const prevTimestamp = dayjs(datetimeObjToISOString(prev.start_date)).unix();

    return Math.abs(currTimestamp - now) < Math.abs(prevTimestamp - now)
      ? curr
      : prev;
  });

  return closest;
};
